export const RECOVERY_STATE_SCHEMA_VERSION = 1 as const;

export type RecoveryPhase =
  | 'capture_pending'
  | 'capturing'
  | 'captured'
  | 'uploading'
  | 'awaiting_validation'
  | 'validated'
  | 'reset_authorized'
  | 'pending_archived'
  | 'local_cleared'
  | 'canonical_pulled'
  | 'history_restored'
  | 'verifying'
  | 'completed';

export type PendingRecoveryClassification =
  | 'server_applied'
  | 'historical_materialized'
  | 'conflict_materialized'
  | 'escrowed_verified'
  | 'superseded_by_remote_entity'
  | 'unaccounted';

export const TERMINAL_PENDING_RECOVERY_CLASSIFICATIONS = [
  'server_applied',
  'historical_materialized',
  'conflict_materialized',
  'escrowed_verified',
  'superseded_by_remote_entity',
] as const satisfies readonly PendingRecoveryClassification[];

export interface RecoveryIdentity {
  companyId: string;
  userId: string;
  deviceId: string;
}

export interface PendingRecoveryItem {
  pendingId: string;
  sourceDigest: string;
  classification: PendingRecoveryClassification;
  classifiedAt: string | null;
  evidenceRef: string | null;
  reason: string | null;
}

export interface RecoveryMediaDescriptor {
  mediaId: string;
  checklistId: string | null;
  fieldId: string | null;
  sourceUri: string;
  byteLength: number;
  plaintextSha512: string;
  contentType: string;
}

export type RecoveryEscrowPartKind = 'database' | 'media';

export interface RecoveryEscrowObjectDescriptor {
  objectPath: string;
  localUri: string;
  ciphertextBytes: number;
  ciphertextSha512: string;
  contentType: string;
}

export interface RecoveryEscrowPart extends RecoveryEscrowObjectDescriptor {
  partId: string;
  index: number;
  kind: RecoveryEscrowPartKind;
  plaintextBytes: number;
  plaintextSha512: string;
  mediaId: string | null;
}

export interface RecoveryEscrowManifest {
  schema: 'app-guincho-recovery-escrow/v1';
  recoveryId: string;
  identity: RecoveryIdentity;
  generatedAt: string;
  logicalSha512: string;
  partCount: number;
  totalCiphertextBytes: number;
  tableCounts: Record<string, number>;
  pendingItemCount: number;
  media: RecoveryMediaDescriptor[];
  parts: RecoveryEscrowPart[];
}

export interface PreparedRecoveryEscrow {
  manifest: RecoveryEscrowManifest;
  manifestObject: RecoveryEscrowObjectDescriptor;
}

export interface RecoveryEscrowProgress {
  prepared: PreparedRecoveryEscrow;
  verifiedPartIds: string[];
  manifestVerified: boolean;
  validationRequestedAt: string | null;
}

export interface RemoteRecoveryReceipt {
  schema: 'app-guincho-recovery-receipt/v1';
  receiptId: string;
  recoveryId: string;
  companyId: string;
  userId: string;
  deviceId: string;
  manifestSha512: string;
  status: 'validated';
  validatedAt: string;
  expiresAt: string | null;
  authority: string;
  proof: string;
  attestationPayload: {
    schema: 'app-guincho-recovery-attestation/v1';
    validatorKeyId: string;
    validatedAt: string;
    recovery: {
      recoveryId: string;
      companyId: string;
      userId: string;
      deviceId: string;
    };
    manifest: RecoveryRemoteObjectInfo;
    objects: RecoveryRemoteObjectInfo[];
    pendingItems: Array<{
      localOperationId: string;
      entityType: string;
      entityId: string | null;
      payloadSha512: string;
      terminalResolution?: {
        schema: 'app-guincho-pending-terminal-resolution/v1';
        classification: 'superseded_by_remote_entity';
        successorEntityId: string;
        writeSetCount: number;
        writeSetSha512: string;
      };
    }>;
    restoration: {
      logicalSha512: string;
      partCount: number;
      mediaCount: number;
      tableCounts: Record<string, number>;
      pendingItemCount: number;
    };
  };
}

export interface RecoveryReceiptVerification {
  valid: boolean;
  verifiedAt: string;
  authority: string;
  proofDigest: string;
  reason?: string;
}

export interface PersistedValidatedReceipt {
  receipt: RemoteRecoveryReceipt;
  verification: RecoveryReceiptVerification & { valid: true };
}

export interface RecoveryLastError {
  phase: RecoveryPhase;
  message: string;
  occurredAt: string;
}

export interface RecoveryState {
  schemaVersion: typeof RECOVERY_STATE_SCHEMA_VERSION;
  identity: RecoveryIdentity;
  recoveryId: string;
  phase: RecoveryPhase;
  createdAt: string;
  updatedAt: string;
  consentedAt: string;
  pendingItems: Record<string, PendingRecoveryItem>;
  escrow: RecoveryEscrowProgress | null;
  validatedReceipt: PersistedValidatedReceipt | null;
  lastError: RecoveryLastError | null;
}

export interface RecoveryGateDecision {
  blocked: boolean;
  phase: RecoveryPhase | 'none' | 'unreadable';
  recoveryId: string | null;
  unaccountedPendingCount: number;
  reasons: string[];
}

export interface RecoveryStateStore {
  load(identity: RecoveryIdentity): Promise<RecoveryState | null>;
  save(identity: RecoveryIdentity, state: RecoveryState): Promise<void>;
}

export interface RecoveryClock {
  now(): string;
}

export interface RecoveryIdFactory {
  create(): string;
}

export interface RecoveryCaptureInput {
  identity: RecoveryIdentity;
  recoveryId: string;
  createdAt: string;
  maxPartBytes: number;
}

export const RECOVERY_SNAPSHOT_TABLE_NAMES = [
  'pending_operations',
  'pending_deletions',
  'templates',
  'template_fields',
  'dropdown_options',
  'checklists',
  'checklist_values',
  'sync_conflicts',
] as const;

export type RecoverySnapshotTableName =
  typeof RECOVERY_SNAPSHOT_TABLE_NAMES[number];

export type RecoverySnapshotRow = Readonly<Record<string, unknown>>;

export interface RecoverySnapshotMediaSource {
  mediaId: string;
  checklistId: string | null;
  fieldId: string | null;
  sourceUri: string;
  contentType: string;
}

export interface RecoveryConsistentSnapshot {
  tables: Record<RecoverySnapshotTableName, readonly RecoverySnapshotRow[]>;
  media: readonly RecoverySnapshotMediaSource[];
}

export interface RecoverySnapshotProvider {
  captureConsistentSnapshot(input: RecoveryCaptureInput): Promise<RecoveryConsistentSnapshot>;
}

export interface RecoveryMediaReader {
  readLocalBytes(source: RecoverySnapshotMediaSource): Promise<Uint8Array>;
}

export interface RecoveryRandomSource {
  getBytes(length: number): Promise<Uint8Array>;
}

export interface RecoveryPersistedObject {
  relativePath: string;
  bytes: Uint8Array;
}

/**
 * O commit deve ser atomico: depois de retornar, todos os objetos e o indice
 * preparado precisam existir; em falha, nenhum indice parcial pode ser visivel.
 */
export interface RecoveryCaptureStore {
  loadPrepared(
    identity: RecoveryIdentity,
    recoveryId: string
  ): Promise<PreparedRecoveryEscrow | null>;
  readPersistedObject(localUri: string): Promise<Uint8Array | null>;
  commitPreparedAtomically(input: {
    identity: RecoveryIdentity;
    recoveryId: string;
    prepared: PreparedRecoveryEscrow;
    objects: readonly RecoveryPersistedObject[];
  }): Promise<void>;
}

/**
 * A implementação concreta deve capturar um snapshot consistente, incluir os
 * bytes de mídias locais e produzir partes criptografadas imutáveis.
 */
export interface RecoveryCaptureAdapter {
  prepare(input: RecoveryCaptureInput): Promise<PreparedRecoveryEscrow>;
}

export interface RecoveryRemoteObjectInfo {
  objectPath: string;
  ciphertextBytes: number;
  ciphertextSha512: string;
}

export interface RecoveryEscrowTransport {
  inspectObject(objectPath: string): Promise<RecoveryRemoteObjectInfo | null>;
  uploadImmutable(object: RecoveryEscrowObjectDescriptor): Promise<void>;
}

/** O cliente apenas solicita validação; ele nunca define o resultado. */
export interface RecoveryRemoteValidationGateway {
  registerUpload(input: {
    identity: RecoveryIdentity;
    recoveryId: string;
    prepared: PreparedRecoveryEscrow;
    idempotencyKey: string;
  }): Promise<void>;
  requestValidation(input: {
    identity: RecoveryIdentity;
    recoveryId: string;
    prepared: PreparedRecoveryEscrow;
    idempotencyKey: string;
  }): Promise<void>;
  fetchReceipt(input: {
    identity: RecoveryIdentity;
    recoveryId: string;
  }): Promise<RemoteRecoveryReceipt | null>;
}

export interface RecoveryReceiptVerifier {
  verify(
    receipt: RemoteRecoveryReceipt,
    expected: {
      identity: RecoveryIdentity;
      recoveryId: string;
      manifestSha512: string;
    }
  ): Promise<RecoveryReceiptVerification>;
}

export type RecoveryCheckpointName =
  | 'capture_started'
  | 'capture_persisted'
  | 'part_verified'
  | 'manifest_verified'
  | 'validation_requested'
  | 'receipt_persisted'
  | 'reset_authorized'
  | 'pending_archived'
  | 'local_cleared'
  | 'canonical_pulled'
  | 'history_restored'
  | 'recovery_verified';

export interface RecoveryCheckpointEvent {
  name: RecoveryCheckpointName;
  identity: RecoveryIdentity;
  recoveryId: string;
  phase: RecoveryPhase;
  detailId?: string;
}

export type RecoveryCheckpoint = (event: RecoveryCheckpointEvent) => Promise<void>;

export interface RecoveryResetContext {
  identity: RecoveryIdentity;
  recoveryId: string;
  receipt: PersistedValidatedReceipt;
  pendingItems: PendingRecoveryItem[];
  idempotencyKey: string;
}

/**
 * Cada método deve ser idempotente. Este contrato não contém implementação
 * ligada ao banco local; a integração destrutiva será feita em outro commit.
 */
export interface SafeResetExecutor {
  archiveAccountedPending(context: RecoveryResetContext): Promise<void>;
  verifyCanonicalReady(context: RecoveryResetContext): Promise<void>;
  clearLocalData(context: RecoveryResetContext): Promise<void>;
  ensureFreshRuntime?(): Promise<void>;
  pullCanonicalState(context: RecoveryResetContext): Promise<void>;
  restoreHistoricalState(context: RecoveryResetContext): Promise<void>;
  verifyRestoredState(context: RecoveryResetContext): Promise<{
    valid: boolean;
    unaccountedPendingCount: number;
    details?: Record<string, number | string | boolean | null>;
  }>;
}

export interface RecoveryConnectivity {
  isOnline(): Promise<boolean>;
}

export interface SafeResetDecision {
  allowed: boolean;
  reasons: string[];
  state: RecoveryState | null;
}
